import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import FlagRoundedIcon from "@mui/icons-material/FlagRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import {
    getIconForLanguage,
    getNameForLanguage,
} from "crqlar-react-common/dist/utils/translations.util";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";

interface Props {
    disabled?: boolean;
    languages: string[];
    onChange: (language: string) => void;
    value: string;
}

const LanguageFlag = ({language}: {language: string}) => {
    const source = getIconForLanguage(language);
    const [loadFailed, setLoadFailed] = useState(false);

    useEffect(() => setLoadFailed(false), [source]);

    if (!source || loadFailed) {
        return <FlagRoundedIcon sx={{color: "text.secondary", fontSize: 18}}/>;
    }

    return (
        <Box
            alt=""
            aria-hidden="true"
            component="img"
            onError={() => setLoadFailed(true)}
            src={source}
            sx={{
                border: "0.5px solid",
                borderColor: "divider",
                borderRadius: 0.5,
                display: "block",
                flexShrink: 0,
                height: 14,
                objectFit: "cover",
                width: 20,
            }}
        />
    );
};

const PrintoutLanguageMenu = ({disabled, languages, onChange, value}: Props) => {
    const {t} = useTranslation();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const options = languages.includes(value) ? languages : [value, ...languages];
    const open = Boolean(anchorEl);
    const label = getNameForLanguage(value, true) || value.toUpperCase();

    const selectLanguage = (language: string) => {
        onChange(language);
        setAnchorEl(null);
    };

    return (
        <>
            <Button
                aria-controls={open ? "printout-language-menu" : undefined}
                aria-expanded={open ? "true" : undefined}
                aria-haspopup={"menu"}
                disabled={disabled}
                endIcon={<ArrowDropDownRoundedIcon/>}
                onClick={(event) => setAnchorEl(event.currentTarget)}
                startIcon={<LanguageFlag language={value}/>}
                variant={"text"}
            >
                {label}
            </Button>

            <Menu
                id={"printout-language-menu"}
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                MenuListProps={{"aria-label": t("Layout language")}}
            >
                {options.map((language) => {
                    const selected = language === value;

                    return (
                        <MenuItem
                            key={language}
                            selected={selected}
                            onClick={() => selectLanguage(language)}
                        >
                            <ListItemIcon sx={{minWidth: 32}}>
                                <LanguageFlag language={language}/>
                            </ListItemIcon>
                            <ListItemText>
                                {getNameForLanguage(language, true) || language.toUpperCase()}
                            </ListItemText>
                            {selected && (
                                <CheckRoundedIcon
                                    color="primary"
                                    fontSize="small"
                                    sx={{ml: 2}}
                                />
                            )}
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
};

export default PrintoutLanguageMenu;
